import { useEffect, useRef } from 'react'

type Star = { x: number; y: number; z: number; tw: number; sparkle: string | null }

const SPARKLES = ['#6fe3ff', '#f6b418', '#ff7ae8', '#ffffff']

// Canvas sky. `drift` > 0 gives the cracktro's layered parallax; 0 is the board's still,
// twinkling night. Stars are drawn as whole pixels so they stay in the pixel world.
export function Starfield({
	width,
	height,
	count = 180,
	drift = 0,
	className,
}: {
	width: number
	height: number
	count?: number
	drift?: number
	className?: string
}) {
	const ref = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const ctx = ref.current?.getContext('2d')
		if (!ctx) return
		const stars: Star[] = Array.from({ length: count }, () => {
			const z = 1 + Math.floor(Math.random() * 3)
			return {
				x: Math.random() * width,
				y: Math.random() * height,
				z,
				tw: Math.random() * Math.PI * 2,
				sparkle: z === 3 && Math.random() < 0.35 ? SPARKLES[Math.floor(Math.random() * SPARKLES.length)] : null,
			}
		})
		const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
		let raf = 0
		let last = performance.now()

		const frame = (now: number) => {
			const dt = Math.min(0.05, (now - last) / 1000)
			last = now
			ctx.clearRect(0, 0, width, height)
			for (const s of stars) {
				if (!reduced) {
					s.x -= drift * s.z * dt
					if (s.x < 0) s.x += width
					s.tw += dt * (1 + s.z)
				}
				const a = 0.35 + 0.25 * s.z * (0.6 + 0.4 * Math.sin(s.tw))
				const x = Math.round(s.x)
				const y = Math.round(s.y)
				if (s.sparkle) {
					// the nearest stars glint as coloured pixel crosses
					const arm = a > 0.9 ? 4 : 2
					ctx.fillStyle = s.sparkle
					ctx.globalAlpha = Math.min(1, a)
					ctx.fillRect(x - arm, y, arm * 2 + 2, 2)
					ctx.fillRect(x, y - arm, 2, arm * 2 + 2)
					ctx.fillStyle = '#ffffff'
					ctx.fillRect(x, y, 2, 2)
					ctx.globalAlpha = 1
					continue
				}
				ctx.fillStyle = s.z === 3 ? `rgba(255,236,190,${a})` : `rgba(170,190,255,${a})`
				const size = s.z === 3 ? 3 : 2
				ctx.fillRect(x, y, size, size)
			}
			raf = requestAnimationFrame(frame)
		}
		raf = requestAnimationFrame(frame)
		return () => cancelAnimationFrame(raf)
	}, [width, height, count, drift])

	return <canvas ref={ref} width={width} height={height} className={className} aria-hidden />
}
